import React, { useState, useEffect } from "react";
import { Typography, Button } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import markdownit from "markdown-it";
import PdfDownloadButton from "./PdfDownloadButton";
import hljs from 'highlight.js';
import katex from 'katex';
import texmath from 'markdown-it-texmath';

// Import KaTeX CSS
import 'katex/dist/katex.min.css';

// Initialize markdown-it with HTML and line breaks enabled
const md = markdownit('default', {
    html: true,
    breaks: true,
    linkify: true,
    typographer: true,
    highlight: function (str: string, lang: string) {
        if (lang && hljs.getLanguage(lang)) {
            try {
                return '<pre><code class="hljs">' +
                    hljs.highlight(str, { language: lang, ignoreIllegals: true }).value +
                    '</code></pre>';
            } catch (__) { }
        }
        return '';
    }
});

// Add LaTeX support
md.use(texmath, {
    engine: katex,
    delimiters: 'dollars',
    katexOptions: { macros: { "\\RR": "\\mathbb{R}" } }
});

export interface MarkdownCardProps {
    content: string;
    showButtons?: boolean;
}

// Configure markdown renderer for images
const configureMarkdown = () => {
    md.renderer.rules.image = (tokens, idx, options, env, self) => {
        const token = tokens[idx];
        let src = '';
        let alt = '';

        // Safely handle attrs that might be null
        if (token.attrs) {
            const srcIndex = token.attrIndex('src');
            src = srcIndex >= 0 ? token.attrs[srcIndex][1] : '';
            const altIndex = token.attrIndex('alt');
            alt = altIndex >= 0 ? token.attrs[altIndex][1] : '';
        }

        // Ensure link is a valid image URL or image service
        if (
            src &&
            (src.match(/\.(jpeg|jpg|gif|png|svg|webp)$/i) ||
                src.includes('image') ||
                src.includes('img') ||
                src.includes('aliyun') ||
                src.includes('oss'))
        ) {
            // Return styled image tag
            return `<img src="${src}" alt="${alt}" class="markdown-image" />`;
        } else {
            // If not an image link, return link text
            return `<a href="${src}" target="_blank">${alt || src}</a>`;
        }
    };

    return md;
};

const MarkdownCard:
    React.FC<MarkdownCardProps> =
    ({ content, showButtons = false }) => {
        const markdown = configureMarkdown();

        // const renderedContent = content
        const renderedContent = content.replaceAll("```markdown", "")
            .replaceAll("```", "");

        // Handle copying text
        const handleCopyMessage = () => {
            if (!content) {
                console.error("No text content found to copy");
                return;
            }

            // Copy to clipboard
            try {
                navigator.clipboard.writeText(renderedContent)
                    .then(() => {
                        console.log("Reply content copied to clipboard");
                    })
                    .catch(err => {
                        console.error("Copy failed:", err);
                    });
            } catch (error) {
                console.error("Copy operation failed:", error);
            }
        };

        return (
            <>
                <div className="markdown-content">
                    <Typography>
                        <div dangerouslySetInnerHTML={{
                            __html: markdown
                                .render(renderedContent)
                        }} />
                    </Typography>
                </div>

                {showButtons && (
                    <div className="message-buttons-container">
                        <Button
                            type="primary"
                            icon={<CopyOutlined />}
                            onClick={handleCopyMessage}
                            className="message-action-button"
                            style={{ marginRight: '10px' }}
                        >
                            复制
                        </Button>
                        <PdfDownloadButton
                            content={renderedContent}
                        />
                    </div>
                )}
            </>
        );
    };

export default MarkdownCard; 